import { Empty } from "antd";
import { useMemo } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { EntityTypes } from "@vzbl/integrations";
import { Flex } from "src/components/Flex";
import { staticDashboardsPageContext } from "pages/[orgId]/dashboards/context";
import {
  mergeStateIntoDashboard,
  useDashboardRuntime,
} from "pages/[orgId]/dashboards/DashboardsDomain";
import { DashboardV1State, DeepLinkType } from "pages/api/deep-links/types";
import {
  DashboardViewer,
  DEFAULT_SPACING,
  DEFAULT_ROW_HEIGHT,
} from "pages/[orgId]/dashboards/DashboardViewer";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { useApplicationContext } from "src/hooks/useApplication";

export function Insights(props: { entity: any; insightName?: string }) {
  const dashboards = useDeepLinks(DeepLinkType.BOARD);
  if (!dashboards.links.isFetched)
    return (
      <Flex centered style={{ padding: 32 }}>
        <LoadingOutlined />
      </Flex>
    );

  const entityType: string = props.insightName || props.entity.labels[0];
  const systemName = `insight-${entityType}`;
  const named = dashboards.links.data?.find(
    (link) => (link.json as DashboardV1State).systemName === systemName
  );
  return named ? (
    <InsightDashboard
      entity={props.entity}
      json={named.json as DashboardV1State}
    />
  ) : (
    <Flex centered style={{ padding: 32 }}>
      <Empty description="Metrics dashboard not found" />
    </Flex>
  );
}

function InsightDashboard(props: { entity: any; json: DashboardV1State }) {
  const filters = getFilter(props.entity);
  const [runtimeProps] = useDashboardRuntime({
    ...staticDashboardsPageContext,
    dashboardId: "insight",
    source: "embedded",
    initialState: mergeStateIntoDashboard(props.json, {
      orgId: useApplicationContext().org_id!,
      filters,
      duration: props.json.defaultDateRange?.duration,
      startDate: props.json.defaultDateRange?.startDate,
      endDate: props.json.defaultDateRange?.endDate,
    }),
  });

  const height = useMemo(() => {
    return determineBoardHeight(props.json);
  }, [props.json]);

  return (
    <div
      className="dashboards"
      style={{
        height,
        marginLeft: -20,
        marginRight: -20,
      }}
    >
      <DashboardViewer key="viewer" {...runtimeProps} />
    </div>
  );
}

function determineBoardHeight(board: DashboardV1State): number {
  const height = Math.max(...board.layout.map((l) => l.y + l.h));
  const gap = board.spacing === undefined ? DEFAULT_SPACING : board.spacing;
  const multiple = DEFAULT_ROW_HEIGHT + gap;
  const padding = gap * 2;
  return height * multiple + padding;
}

const getFilter = (entity: any) => {
  const entityType = (entity.labels || []).at(0) as keyof typeof EntityTypes;
  // note: not sure whay we don't just do uuid.
  switch (entityType) {
    case EntityTypes.Issue: {
      return [
        {
          key: "Issue.issueKey",
          attribute: "issueKey",
          target: EntityTypes.Issue,
          comparison: {
            IN: [entity.properties.issueKey],
          },
        },
      ];
    }
    case EntityTypes.Branch: {
      return [
        {
          key: "Branch.name",
          attribute: "name",
          target: EntityTypes.Branch,
          comparison: {
            IN: [entity.properties.name],
          },
        },
      ];
    }
    case EntityTypes.Person: {
      return [
        {
          key: "Event.Actor.name",
          attribute: "Activity.Actor",
          target: "Event",
          comparison: {
            IN: [entity.properties.name],
          },
        },
      ];
    }
    case EntityTypes.Repository: {
      return [
        {
          key: "Repository.fullName",
          attribute: "fullName",
          target: EntityTypes.Repository,
          comparison: {
            IN: [entity.properties.fullName],
          },
        },
      ];
    }
    default: {
      return [
        {
          key: `${entityType}.uuid`,
          attribute: "uuid",
          target: EntityTypes[entityType],
          comparison: {
            IN: [entity.properties.uuid],
          },
        },
      ];
    }
  }
};
